import dropin from 'braintree-web-drop-in'
import { $logger } from '@/boot/logger'
import { $pegApi } from '@/boot/peg-api'
import { $rollbar } from '@/boot/rollbar'
import { cloneDeep } from 'lodash-es'

export const usePaymentGatewayStore = defineStore('paymentGateway', () => {
  const $q = useQuasar()
  const appConfigStore = useAppConfigStore()
  const { checkoutConfig } = storeToRefs(appConfigStore)
  const { isCheckoutPage } = useRouteMap()

  const DEFAULT_GATEWAY_STATE = {
    ready: false,
    error: false,
    message: null,
    dropin: {},
    paymentMethodIsValid: false,
    paymentMethodIsAuthed: false,
    paymentMethod: null,
    transaction: null,
    braintree: { nonce: null, token: null, deviceData: null, customer: null },
    ach: {
      nonce: null,
      deviceData: null,
      details: null,
      isValid: false,
      isAuthed: false,
      isVerified: null, // 'pending', 'verified', 'failed'
    },
  }
  const WITH_3DS = false
  let gateway = null

  const initializing = ref(false)
  const isFetchingCustomer = ref(false)
  const gatewayState = reactive(cloneDeep(DEFAULT_GATEWAY_STATE))
  const gatewayAvailablePaymentMethods = computed(() =>
    // ACH is only available for business accounts
    checkoutConfig.value.paymentMethods.filter(method => method !== 'ach' || useUserStore().userHasBusinessAccount)
  )
  const paymentMethodIsSelected = computed(() => !!gatewayState.paymentMethod)
  const achPaymentMethodSelected = computed(() => gatewayState.paymentMethod === 'ach')
  const vaultPaymentMethodSelected = computed(() => gatewayState.paymentMethod === 'vault' && !!selectedVaultMethod.value)
  const selectedVaultMethod = ref(null)
  const cardPaymentMethodSelected = computed(() => gatewayState.paymentMethod === 'card')
  // augment payment methods with additional properties
  const gatewayCustomerPaymentMethods = computed(() => {
    const methods = gatewayState.braintree.customer?.paymentMethods || []

    return methods
      .map(method => {
        const { isACH, isVerified, hasMicroTransfers, microTransferAmountsVerified } = determinePaymentMethodVerification(method)

        // find the matching payment method on the Peg user for enhanced status info
        const internalMethod = (useUserStore().userPaymentMethods || []).find(pm => pm.token === method.token)

        const isDefault = Boolean(method.token === useUserStore().user?.defaultPaymentToken || internalMethod?.default)
        const isFailed = Boolean(internalMethod?.failed)
        const isExpired = Boolean(internalMethod?.expired)
        const isDeleted = Boolean(internalMethod?.deleted)

        return {
          ...method,
          isDefault,
          isFailed,
          isExpired,
          isDeleted,
          isACH,
          isVerified,
          hasMicroTransfers,
          microTransferAmountsVerified,
          id: internalMethod?.id || null,
        }
      })
      .filter(method => !method.isDeleted)
  })
  const gatewayCustomerHasVaultPayment = computed(() => Boolean(gatewayCustomerPaymentMethods.value.length))

  function determinePaymentMethodVerification(method) {
    let isVerified = true
    const isACH = !!method.routingNumber
    const microTransfersVerification = method.verifications.find(v => v.verificationMethod === 'micro_transfers')
    const hasMicroTransfers = !!microTransfersVerification
    const { userPaymentMethods = [] } = useUserStore()
    const microTransferAmountsVerified = userPaymentMethods.find(pm => pm.token === method.token)?.microTransferAmountsVerified

    if (isACH) {
      if (hasMicroTransfers) {
        // micro-transfers verification takes precedence
        isVerified = microTransfersVerification.status === 'verified'
      } else {
        // otherwise check for any verification status
        isVerified = method.verifications.some(v => v.status === 'verified')
      }
    }
    return { isACH, isVerified, hasMicroTransfers, microTransferAmountsVerified }
  }

  /**
   * Full reset of the gateway state
   */
  function resetState() {
    $logger.debug('paymentGateway resetState')
    gatewayUpdateState(cloneDeep(DEFAULT_GATEWAY_STATE))
    selectedVaultMethod.value = null
    initializing.value = false
    isFetchingCustomer.value = false
    gateway = null
  }

  /**
   * Teardown the Drop In UI instance
   */
  async function teardownGateway() {
    if (gateway) {
      try {
        await gateway.teardown()
      } catch (error) {
        // Ignore teardown errors - component may already be torn down
        if (error.code !== 'METHOD_CALLED_AFTER_TEARDOWN') {
          $logger.error('Failed to teardown gateway', error)
        }
      }
      gateway = null
    }
  }

  /**
   * Reset the Drop In UI payment method
   */
  function resetPaymentMethod() {
    $logger.debug('resetPaymentMethod')
    // reset selected vault method
    selectedVaultMethod.value = null
    // reset the dropin UI
    resetGateway()
    // reset the payment method state
    resetPaymentMethodState()
  }

  /**
   * Reset the payment state props
   */
  function resetPaymentMethodState() {
    $logger.debug('resetPaymentMethodState')
    gatewayUpdateState({
      paymentMethod: null,
      paymentMethodIsValid: false,
      paymentMethodIsAuthed: false,
      transaction: null,
    })
    updateBraintreeState({ nonce: null })
  }

  async function resetGateway() {
    if (gateway && cardPaymentMethodSelected.value) {
      gateway.clearSelectedPaymentMethod()
      // teardown and recreate the dropin UI to ensure a clean state
      await teardownGateway()
      await createPaymentUI()
    }
  }

  /**
   * Update the gateway state
   * @param {Object} newState - The state to update
   */
  function gatewayUpdateState(newState) {
    Object.assign(gatewayState, newState)
  }

  function updateBraintreeState(newState) {
    Object.assign(gatewayState.braintree, newState)
  }

  function updateACHState(newState) {
    Object.assign(gatewayState.ach, newState)
  }

  /**
   * Set the selected payment method
   */
  function setPaymentMethod(paymentMethod, options) {
    const { isValid = false, reset = true } = options || {}
    $logger.debug('setPaymentMethod', paymentMethod)

    // reset the payment method state
    if (reset) {
      resetPaymentMethod()
    }

    gatewayUpdateState({ paymentMethod, paymentMethodIsValid: isValid })
  }

  function validatePaymentMethod() {
    gatewayUpdateState({ paymentMethodIsValid: true })
  }

  /**
   * Generate the required auth
   */
  async function getClientToken() {
    $logger.debug('getClientToken')

    try {
      const resp = await $pegApi.PaymentGateway.createToken({ customerId: gatewayState.braintree.customer?.id })

      if (resp.token) {
        updateBraintreeState({ token: resp.token })
      }

      // $logger.debug('getClientToken', state)
    } catch (error) {
      $logger.error('PaymentGateway => getClientToken', error)
      $rollbar.error('PaymentGateway => getClientToken', { message: error?.message || 'Unknown error' })
      // Keep the user-facing message generic — gatewayState.message renders in
      // the checkout UI, so don't leak raw backend/network error strings. The
      // detail is captured above in $logger/$rollbar for diagnostics.
      gatewayUpdateState({ error: true, message: 'Uh oh...something went wrong. Please try again.' })
    }
  }

  /**
   * Create the payment UI
   */
  async function createPaymentUI(retry = true) {
    const { totalPrice: checkoutTotalPrice } = useCheckoutStore().checkout || {}

    // await dom update to ensure container is rendered
    await nextTick()

    $logger.debug('createPaymentUI', gatewayAvailablePaymentMethods.value)

    try {
      let options = {
        authorization: gatewayState.braintree.token,
        container: '#payment_options',
        dataCollector: true,
        threeDSecure: WITH_3DS,
        paymentMethodPriority: ['card', 'paypal', 'applePay', 'googlePay', 'venmo'],
        vaultManager: false,
        // suppress drop-in vaulted payment methods UI - we handle these separately
        hiddenVaultedPaymentMethodTypes: ['card', 'paypal', 'paypalCredit'],
        preselectVaultedPaymentMethod: false,
      }

      if (gatewayAvailablePaymentMethods.value.includes('card')) {
        options.card = {
          // cardholderName: { required: true },
          vault: {
            allowVaultCardOverride: isCheckoutPage.value, // default is to always vault the card - only allow disabling on checkout page
          },
          overrides: {
            fields: {
              // ONLY PRE-FILL FOR TESTING
              // number: {
              //   prefill: import.meta.env.DEV ? '4111111111111111' : null,
              // },
              // expirationDate: {
              //   prefill: import.meta.env.DEV ? '10/26' : null,
              // },
              // cvv: {
              //   prefill: import.meta.env.DEV ? '123' : null,
              // },
              postalCode: {
                // label: 'ZIP Code',
                // placeholder: 'ZIP Code',
                // internalLabel: 'ZIP Code',
                minlength: 5,
                maxlength: 5,
                type: 'number',
                // TODO: prefilling the zip code will cause the default payment method to NOT be requestable
                // prefill: billingAddress.value?.zip || null,
              },
            },
          },
        }
      }

      // add more payment methods if on checkout page
      if (gatewayAvailablePaymentMethods.value.includes('applePay')) {
        options.applePay = {
          displayName: 'Rush ReCommerce Stores',
          paymentRequest: {
            total: {
              amount: checkoutTotalPrice,
              label: 'Peg, Inc.',
            },
          },
        }
      }

      if (gatewayAvailablePaymentMethods.value.includes('googlePay')) {
        options.googlePay = {
          googlePayVersion: 2,
          merchantId: import.meta.env.VITE_GOOGLE_PAY_MERCHANT_ID,
          transactionInfo: {
            totalPriceStatus: 'FINAL',
            totalPrice: (checkoutTotalPrice || 0).toString(),
            currencyCode: 'USD',
          },
          allowedPaymentMethods: [
            {
              type: 'CARD',
              parameters: {
                // We recommend collecting and passing billing address information with all Google Pay transactions as a best practice.
                billingAddressRequired: true,
                billingAddressParameters: {
                  format: 'FULL',
                },
              },
            },
          ],
        }
      }

      if (gatewayAvailablePaymentMethods.value.includes('paypal')) {
        options.paypal = {
          flow: 'checkout',
          amount: checkoutTotalPrice,
          currency: 'USD',
          displayName: 'Peg, Inc.',
          vault: {
            vaultPayPal: false,
          },
          // intent: 'capture',
          // shippingAddressOverride: {
          //   recipientName: checkoutState.value.shippingAddress.firstName + ' ' + checkoutState.value.shippingAddress.lastName,
          //   line1: checkoutState.value.shippingAddress.address1,
          //   line2: checkoutState.value.shippingAddress.address2,
          //   city: checkoutState.value.shippingAddress.city,
          //   countryCode: 'US',
          //   postalCode: checkoutState.value.shippingAddress.zip,
          //   state: statesUtil.nameToCode(checkoutState.value.shippingAddress.state),
          //   phone: checkoutState.value.shippingAddress.phone,
          // },
          // shippingAddressEditable: false,
          // landingPageType: 'billing',
        }
      }

      if (gatewayAvailablePaymentMethods.value.includes('venmo')) {
        options.venmo = {}
      }

      await teardownGateway()

      // create the dropin gateway
      gateway = await dropin.create(options)

      // payment option selected
      gateway.on('paymentOptionSelected', event => {
        $logger.debug('paymentOptionSelected', event)
        setPaymentMethod(event.paymentOption)
      })

      // active view changed
      gateway.on('changeActiveView', event => {
        $logger.debug('changeActiveView', event)
        resetPaymentMethodState()
      })

      // check validity (can be requested)
      gateway.on('paymentMethodRequestable', event => {
        $logger.debug('paymentMethodRequestable', event)
        if (event.type === 'CreditCard' || event.paymentMethodIsSelected) {
          validatePaymentMethod()
        }
      })

      // @dreysolano - removed to fix bug where payment form hides when card is not requestable, like when zip code is invalid
      // TODO: is there some reason to reset payment method state here?
      // gateway.on('noPaymentMethodRequestable', event => {
      //   $logger.debug('noPaymentMethodRequestable', event)
      //   resetPaymentMethodState()
      // })

      // debug validity
      gateway.on('card:validityChange', event => {
        const fieldName = event.emittedBy
        const fieldState = event.fields[fieldName]

        // debugging
        if (fieldState.isValid) {
          $logger.debug(fieldName, 'is fully valid')
        } else if (fieldState.isPotentiallyValid) {
          $logger.debug(fieldName, 'is potentially valid')
        } else {
          $logger.debug(fieldName, 'is not valid')
        }
      })
    } catch (error) {
      // retry one time on failure
      if (retry) {
        $logger.error('createPaymentUI failed, retrying...', error)
        $rollbar.warning('paymentGateway > createPaymentUI retry', { message: error.message || 'Unknown error' })
        return createPaymentUI(false)
      }

      gatewayUpdateState({ error: true, message: 'Uh oh...something went wrong. Please try again.' })
      $logger.error('paymentGateway > createPaymentUI', error)
      $rollbar.error('paymentGateway > createPaymentUI', { message: error.message || 'Unknown error' })
    }

    gatewayUpdateState({ ready: true })
  }

  /**
   * Authorize the selected payment option
   */
  async function gatewayAuthorizePayment() {
    let success = false

    try {
      const resp = await gateway.requestPaymentMethod()

      $logger.debug('requestPaymentMethod', resp)

      // store the nonce and device data
      if (resp && resp.nonce) {
        const { nonce, deviceData } = resp
        updateBraintreeState({ nonce, deviceData })
        success = true
      }
    } catch (error) {
      $rollbar.error('gatewayAuthorizePayment', error)
      $logger.error('gatewayAuthorizePayment', error)
    }

    if (success) {
      gatewayUpdateState({ paymentMethodIsAuthed: true })
    } else {
      gatewayUpdateState({ message: 'Payment authorization failed. Please try again.' })
    }

    return { success }
  }

  /**
   * Set auto checkout
   */
  async function setAutoCheckout(newStatus) {
    const { user, userHasAutoCheckout, updateUser } = useUserStore()
    // set default token if not already set, required for auto checkout
    const defaultPaymentIsVaulted = gatewayCustomerPaymentMethods.value.some(method => method.token === user?.defaultPaymentToken)
    if (gatewayCustomerHasVaultPayment.value && !defaultPaymentIsVaulted) {
      const { success: setDefaultSucces } = await saveUserDefaultPayment(gatewayCustomerPaymentMethods.value[0].token)

      if (!setDefaultSucces) {
        $q.notify({
          type: 'error',
          message: 'Failed to set default payment method',
        })
        return { success: false }
      }
    }

    // update auto checkout status if changed
    if (userHasAutoCheckout !== newStatus) {
      return updateUser({ autoCheckout: newStatus })
    }

    return { success: true }
  }

  /**
   * Set default payment method
   */
  async function saveUserDefaultPayment(token) {
    const currentDefault = gatewayCustomerPaymentMethods.value.find(method => method.isDefault)
    token = token || currentDefault?.token

    if (!token) {
      return { success: false }
    }

    // attempt to update default payment method in Braintree
    // TODO: this does not work currently, need to figure out why and how to update BT customer
    // if (currentDefault?.token !== token) {
    //   await gatewayUpdateCustomer({ defaultPaymentMethodToken: token })
    // }

    const { success } = await useUserStore().saveUserDefaultPayment(token)

    if (!success) {
      $q.notify({
        type: success ? 'success' : 'error',
        message: success ? 'Default payment method updated' : 'Failed to update default payment method',
      })
    }

    return { success }
  }

  /**
   * Get the gateway customer instance
   */
  async function gatewayGetCustomer(options = {}) {
    const { force = false } = options

    if (isFetchingCustomer.value || (gatewayState?.braintree?.customer?.id && !force)) {
      return gatewayState.braintree.customer
    }

    isFetchingCustomer.value = true
    let resp
    try {
      resp = await $pegApi.PaymentGateway.getCustomer()
    } catch (error) {
      isFetchingCustomer.value = false
      // a 404 simply means the customer has not been created yet — not an error
      if (error?.statusCode !== 404) {
        $rollbar.error('PaymentGateway => gatewayGetCustomer', error)
      }
      return null
    }

    $logger.debug('gatewayGetCustomer', resp)

    // PEG-826: saveUserDefaultPayment() -> useUserStore().saveUserDefaultPayment()
    // -> Users.update() now rejects on API errors. If the post-fetch default-
    // payment sync threw, the trailing isFetchingCustomer reset was skipped,
    // leaving the store stuck "fetching" so every future gatewayGetCustomer()
    // short-circuited. Wrap the post-fetch work in try/finally so the flag is
    // always cleared regardless of any rejection.
    try {
      updateBraintreeState({ customer: resp.customer })

      const { user, syncGatewayPaymentMethods } = useUserStore()
      // sync payment methods with Peg user
      await syncGatewayPaymentMethods(gatewayState.braintree.customer?.paymentMethods || [])

      // ensure user has a default payment method
      if (!user?.defaultPaymentToken && gatewayCustomerPaymentMethods.value.length) {
        await saveUserDefaultPayment(gatewayCustomerPaymentMethods.value[0].token)
      }
    } catch (error) {
      $rollbar.error('PaymentGateway => gatewayGetCustomer post-fetch sync', error)
    } finally {
      isFetchingCustomer.value = false
    }

    return resp.customer || null
  }

  /**
   * Refresh the gateway customer instance
   */
  function refreshGatewayCustomer() {
    return gatewayGetCustomer({ force: true })
  }

  /**
   * Update the gateway customer instance
   */
  async function gatewayUpdateCustomer(data = {}) {
    const { deviceData, customer, nonce } = gatewayState.braintree
    const { user } = useUserStore()
    // const billingAddress = {
    //   firstName: user.firstName,
    //   lastName: user.lastName,
    //   streetAddress: user.billingAddress1,
    //   extendedAddress: user.billingAddress2,
    //   locality: user.billingCity,
    //   region: user.billingState,
    //   postalCode: user.billingZip,
    //   countryCodeAlpha2: 'US',
    //   options: { updateExisting: true },
    // }

    // keep data to update for PUT requests as API passes thru whatever client sends
    const dataToUpdate = {
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      phone: user.phone,
      deviceData,
      ...data,
    }

    // if (token) {
    //   dataToUpdate.creditCard = { options: { updateExistingToken: token } }
    // }

    let resp

    try {
      if (customer?.id) {
        resp = await $pegApi.PaymentGateway.updateCustomer(dataToUpdate)
      } else {
        // set nonce if not already defined
        if (nonce && !data.paymentMethodNonce) {
          dataToUpdate.paymentMethodNonce = nonce
        }
        const { website, deviceData, paymentMethodNonce } = dataToUpdate

        // API only accepts `deviceData`, `website` and `paymentMethodNonce` on create
        resp = await $pegApi.PaymentGateway.createCustomer({ website, deviceData, paymentMethodNonce })
      }
    } catch (error) {
      $rollbar.error('PaymentGateway => gatewayUpdateCustomer', error)
      return { success: false, message: error?.message || 'Unknown error' }
    }

    await refreshGatewayCustomer()

    $logger.debug('gatewayUpdateCustomer', resp)

    return resp
  }

  /**
   * Process ACH payment method addition
   */
  async function gatewaySaveACHPaymentMethod(achData) {
    const { nonce, deviceData, details } = achData // `savePaymentMethod` removed for now

    try {
      // Update ACH state
      updateACHState({
        nonce,
        deviceData,
        details,
        isValid: true,
        isAuthed: true,
        isVerified: false,
      })

      // Save ACH payment method
      // inpect response to see if additional verification is needed (i.e. micro transaction verification)
      const resp = await $pegApi.PaymentGateway.saveACHPaymentMethod({ nonce })

      refreshGatewayCustomer()
      return resp
    } catch (error) {
      $rollbar.error('Failed to save ACH payment', error)
      return { success: false, message: error.message || 'Failed to save ACH payment method' }
    }
  }

  /**
   * Verify ACH payment method with micro-transfers
   */
  async function verifyACHDeposits(token, verificationData) {
    let resp
    try {
      // Call backend endpoint to verify micro-transfers. This is the actual
      // gateway verification — its success/failure is what determines whether
      // the ACH account is verified.
      resp = await $pegApi.PaymentGateway.verifyACHMicroTransfers(verificationData)
      updateACHState({ isVerified: true })
    } catch (error) {
      $rollbar.error('Failed to verify ACH payment', error)
      updateACHState({ isVerified: false })
      // Preserve error.data (carries the verification status, e.g. TOO_MANY) so
      // the dialog can still map expected 4xx outcomes to specific messages.
      // Pre-PEG-826 the non-throwing path returned this same { data } shape.
      return { success: false, message: error?.message, data: error?.data ?? null }
    }

    // Post-verification sync of the PEG-side payment-method record. The gateway
    // verification above has already succeeded, so a failure here must NOT flip
    // isVerified back to false or report the verification as failed — it is a
    // best-effort downstream sync. Log and continue.
    try {
      await refreshGatewayCustomer()

      const { userPaymentMethods = [], updateUserPaymentMethod } = useUserStore()
      // find the id of the user payment method that matches the verified payment method
      const verifiedMethod = userPaymentMethods.find(pm => pm.token === token)

      // Update Peg user payment method as verified
      await updateUserPaymentMethod(verifiedMethod.id, { microTransferAmountsVerified: true })
    } catch (error) {
      $rollbar.error('ACH verified at gateway but PEG payment-method sync failed', error)
    }

    return resp
  }

  /**
   * Set the payment nonce for a vaulted payment method
   * @param {Object} method - The payment method object
   * @returns {Promise<Object>} - Result with nonce or error
   */
  async function selectVaultPaymentMethod(method) {
    // vault payment method is being unset
    if (!method || !method.token) {
      selectedVaultMethod.value = null
      return { success: true }
    }

    $logger.debug('Selected stored payment method', {
      token: method.token,
      type: method.isACH ? 'ACH' : 'Card',
    })

    // Generate nonce from the selected payment method token
    const { success, nonce } = await generateNonceFromToken(method.token)

    if (success && nonce) {
      // setPaymentMethod resets state, including selectedVaultMethod, so must come first
      setPaymentMethod('vault', { isValid: true })
      selectedVaultMethod.value = method
      updateBraintreeState({ nonce })
    } else {
      $rollbar.error('Failed to select vault payment method', { token: method.token })
      resetPaymentMethod()
    }

    return { success }
  }

  /**
   * Generate nonce from stored payment method token
   * @param {string} paymentMethodToken - The payment method token
   * @returns {Promise<Object>} - Result with nonce or error
   */
  async function generateNonceFromToken(paymentMethodToken) {
    if (!paymentMethodToken) {
      return { success: false, message: 'Payment method token is required' }
    }

    try {
      $logger.debug('Generating nonce from payment method token', { token: paymentMethodToken })

      const resp = await $pegApi.PaymentGateway.generateNonce(paymentMethodToken)

      if (resp.nonce) {
        $logger.debug('Nonce generated successfully from stored payment method')

        return {
          success: true,
          nonce: resp.nonce,
        }
      } else {
        throw new Error(resp.message || 'Failed to generate nonce')
      }
    } catch (error) {
      const errorMessage = error.message || 'Failed to generate nonce from stored payment method'

      $rollbar.error('Failed to generate nonce from token:', error)

      return { success: false, message: errorMessage }
    }
  }

  /**
   * Reset ACH payment state
   */
  function resetACHPayment() {
    updateACHState({
      nonce: null,
      deviceData: null,
      details: null,
      isValid: false,
      isAuthed: false,
      isVerified: null,
    })
  }

  /**
   * Initialize the gateway
   */
  async function init(options = {}) {
    const { customerOnly = false } = options
    if (initializing.value) {
      return
    }

    initializing.value = true

    // first attempt to get the customer
    await gatewayGetCustomer()

    if (!gatewayState.braintree.customer?.id) {
      // create the customer in Braintree to ensure they can immediately store payment methods
      await gatewayUpdateCustomer()
    }

    if (!customerOnly) {
      await getClientToken()
      await createPaymentUI()
    }

    initializing.value = false
  }

  return {
    gatewayState,
    selectedVaultMethod,
    // getters
    achPaymentMethodSelected,
    vaultPaymentMethodSelected,
    cardPaymentMethodSelected,
    gatewayCustomerPaymentMethods,
    gatewayAvailablePaymentMethods,
    gatewayCustomerHasVaultPayment,
    isFetchingCustomer,
    // actions
    gatewayInit: init,
    gatewayAuthorizePayment,
    gatewayUpdateCustomer,
    gatewayGetCustomer,
    selectVaultPaymentMethod,
    saveUserDefaultPayment,
    setAutoCheckout,
    resetState,
    resetPaymentMethod,
    setPaymentMethod,
    paymentMethodIsSelected,
    // ACH-specific methods
    gatewaySaveACHPaymentMethod,
    verifyACHDeposits,
    resetACHPayment,
    updateACHState,
  }
})
